<template>
  <div class="audio-player-container" :class="{ hasPlaylist: !hidePlaylist }">
    <div class="audio-player">
      <div class="wrapper">
        <div class="playlist" v-if="!hidePlaylist">
          <div
            class="playlistItem"
            :class="{ active: currentTrackId === track.id }"
            @click="handlePlaylistItemClick(track)"
            v-for="(track, index) in tracks"
            :key="track.id"
          >
            <div class="playlistItemTitle">{{ track.title }}</div>
            <div class="playlistItemIndex">
              Del {{ index + 1 }} / {{ tracks.length }}
            </div>
          </div>
        </div>

        <div class="mainPlayer">
          <div class="info">
            <div class="inner" v-if="currentTrack">
              <h3 v-if="!hideTitle" class="audio-title">
                {{ currentTrack.title }}
              </h3>
              <h5 v-if="partOfAudioWalk" class="part-of-audio-walk">Del av ljudvandring</h5>
              <p>{{ currentTrack.description }}</p>
            </div>
          </div>

          <div class="controlsContainer">
            <div class="controlsWrapper">
              <button class="play" @click="handlePlayClick">
                <i
                  class="fas"
                  :class="currentlyPlaying ? 'fa-pause' : 'fa-play'"
                ></i>
              </button>

              <div class="progressWrapper">
                <div class="progressTime">
                  <span class="currentTime">{{
                    currentTime | niceTimeFormat
                  }}</span>
                </div>

                <div
                  class="progressBar audioProgressbar"
                  :class="{ isDragging: isDragging === 'progress' }"
                  ref="progressbar"
                  @mousedown="startDrag"
                  @touchstart="startDrag"
                >
                  <div
                    class="progress"
                    :style="{ width: currentProgressBar + '%' }"
                  ></div>
                  <div
                    class="knob"
                    ref="draggable"
                    :style="{ left: currentProgressBar + '%' }"
                  ></div>
                </div>

                <div class="progressTime">
                  <span class="duration">{{
                    trackDuration | niceTimeFormat
                  }}</span>
                </div>
              </div>

              <div
                class="volumeWrapper"
                :class="{ isDragging: isDragging === 'volume' }"
                v-if="!isIOS"
              >
                <div class="volumeProgress progressWrapper">
                  <div
                    class="progressBar volumeProgressbar"
                    :class="{ isDragging: isDragging === 'volume' }"
                    ref="volumeProgressbar"
                    @mousedown="(e) => startDrag(e, true)"
                    @touchstart="(e) => startDrag(e, true)"
                  >
                    <div
                      class="progress"
                      :style="{ width: currentAudioVolume + '%' }"
                    ></div>
                    <div
                      class="knob volumeKnob"
                      ref="draggable"
                      :style="{ left: currentAudioVolume + '%' }"
                    ></div>
                  </div>
                </div>
                <i
                  class="fas"
                  :class="
                    currentAudioVolume === 0
                      ? 'fa-volume-mute'
                      : currentAudioVolume >= 40
                      ? 'fa-volume-up'
                      : 'fa-volume-down'
                  "
                />
              </div>
            </div>

            <button
              class="transcriptBtn"
              @click="transcriptEnabled = !transcriptEnabled"
            >
              Transkribering
              <i
                class="fas"
                :class="transcriptEnabled ? 'fa-chevron-up' : 'fa-chevron-down'"
              ></i>
            </button>

            <div
              class="transcription"
              :class="{ open: transcriptEnabled }"
              v-if="transcriptEnabled"
            >
              {{ currentTrack.transcription }}
            </div>
          </div>

          <div
            v-if="!hidePlaylist"
            class="d-flex justify-content-end my-2"
          >
            <button
              class="next"
              @click="handleNextClick"
              :disabled="!canGoNext"
            >
              <span v-if="canGoNext">Nästa ljud: {{ nextTrack.title }} <i class="fas fa-angle-double-right"></i></span>
              <span v-else>&nbsp;</span>
            </button>
          </div>

          <div class="mobilePlaylist" v-if="tracks.length > 1">
            <div class="playlist">
              <div
                class="playlistItem"
                :class="{ active: currentTrackId === track.id }"
                @click="handlePlaylistItemMobileClick(track)"
                v-for="(track, index) in tracks"
                :key="track.id"
              >
                <div class="playlistItemTitle">{{ track.title }}</div>
                <div class="playlistItemIndex">
                  Del {{ index + 1 }} / {{ tracks.length }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  props: ['tracks', 'hidePlaylist', 'hideTitle', 'partOfAudioWalk'],

  data: function () {
    return {
      audioPlayerId: null,
      audio: new Audio(),
      currentTrackId: this.tracks[0].id,
      currentTime: 0,
      currentProgressBar: 0,
      currentAudioVolume: 100,
      trackDuration: 0,
      currentTimeId: null,
      transcriptEnabled: false,
      mobilePlayList: false,

      isDragging: null,
      startx: 0,
      startLeft: 0
    }
  },

  mounted () {
    // Set audioPlayerId to a string of tracks id
    this.audioPlayerId = this.tracks.map((track) => track.id).join('-')
    this.changeTrack()
    this.currentAudioVolume = this.audioVolume

    if (!this.hidePlaylist) {
      this.$store.dispatch('setPlayStateAudioPlayerId', this.audioPlayerId)
      this.$store.dispatch('setPlayStateTrackId', this.currentTrackId)
    }
  },

  filters: {
    niceTimeFormat: function (s) {
      return (s - (s %= 60)) / 60 + (s > 9 ? ':' : ':0') + s
    }
  },

  methods: {
    ...mapActions(['setPlayStatePlaying', 'setPlayStateTrackId', 'setPlayStateAudioPlayerId']),
    handlePlaylistItemClick (track) {
      this.currentTrackId = track.id
      this.$store.dispatch('setPlayStateTrackId', this.currentTrackId)
    },
    handlePlaylistItemMobileClick (track) {
      this.handlePlaylistItemClick(track)
      this.showMobilePlaylist()
    },
    handlePlayClick () {
      if (this.currentlyPlaying) {
        this.stopAudio()
      } else {
        this.playAudio()
      }
    },
    handleNextClick () {
      // Find next track in tracks by id and set currentTrackId to that id
      const currentTrackIndex = this.tracks.findIndex(
        (track) => track.id === this.currentTrackId
      )
      const nextTrackIndex = currentTrackIndex + 1
      if (nextTrackIndex < this.tracks.length) {
        this.currentTrackId = this.tracks[nextTrackIndex].id
      }
    },
    showMobilePlaylist () {
      this.mobilePlayList = !this.mobilePlayList
    },
    handlePrevClick () {
      // Find previous track in tracks by id and set currentTrackId to that id
      const currentTrackIndex = this.tracks.findIndex(
        (track) => track.id === this.currentTrackId
      )
      const prevTrackIndex = currentTrackIndex - 1
      if (prevTrackIndex >= 0) {
        this.currentTrackId = this.tracks[prevTrackIndex].id
      }
    },
    changeTrack () {
      this.currentProgressBar = 0

      // Check if player is currently playing, if so, pause it and save that state to wasPlaying
      let wasPlaying = false
      if (this.currentlyPlaying) {
        this.audio.pause()
        wasPlaying = true
        this.updatePlayState({ playing: false })
      }
      // Find track in tracks by id and set audio source to that track
      const track = this.tracks.find(
        (track) => track.id === this.currentTrackId
      )
      this.audio.src = track.url
      this.audio.load()

      var localThis = this
      this.audio.addEventListener('loadedmetadata', function () {
        localThis.trackDuration = Math.round(this.duration)
      })
      this.audio.addEventListener('ended', this.handleEnded)

      if (wasPlaying) {
        this.playAudio()
      }
    },
    playAudio () {
      if (!this.currentlyPlaying) {
        this.getCurrentTimeEverySecond(true)
        this.updatePlayState({ playing: true })
        this.audio.play()
      } else {
        this.stopAudio()
      }
    },
    stopAudio (updatePlayState = true) {
      this.audio.pause()
      updatePlayState && this.updatePlayState({ playing: false })
      clearTimeout(this.currentTimeId)
    },
    handleEnded () {
      // If last track in tracks is playing, stop audio and set currentTrackId to null else play next track
      const currentTrackIndex = this.tracks.findIndex(
        (track) => track.id === this.currentTrackId
      )
      if (currentTrackIndex === this.tracks.length - 1) {
        this.stopAudio()
        this.currentTrackId = null
      } else {
        this.handleNextClick()
      }
    },
    updatePlayState (incomingPayload) {
      const payload = {
        ...this.playState,
        trackId: this.currentTrackId,
        ...incomingPayload,
        audioPlayerId: this.audioPlayerId
      }
      this.$store.commit('setPlayState', payload)
    },
    getCurrentTimeEverySecond: function () {
      var localThis = this
      this.currentTimeId = setTimeout(function () {
        localThis.currentTime = Math.round(localThis.audio.currentTime)
        if (!localThis.isDragging) {
          localThis.currentProgressBar =
            (Math.round(localThis.audio.currentTime) /
              localThis.trackDuration) *
            100
        }
        localThis.getCurrentTimeEverySecond()
      }, 1000)
    },

    startDrag (e, isVolume = false) {
      // Should only use e.preventDefault if event is mousemove
      if (e.type === 'mouseEvent') {
        e.preventDefault()
      }

      clearTimeout(this.currentTimeId)

      const el = isVolume
        ? this.$refs.volumeProgressbar
        : this.$refs.progressbar
      // Set current progress bar to the position of the mouse
      const progressBarLeft = el.getBoundingClientRect().left
      this.startx = e.clientX || e.targetTouches[0].clientX
      this.startLeft = this.startx - progressBarLeft
      this.isDragging = isVolume ? 'volume' : 'progress'

      const currentDragProgress = (this.startLeft / el.offsetWidth) * 100
      // Check if knob has class volumeKnob
      if (isVolume) {
        this.currentAudioVolume =
          currentDragProgress < 0
            ? 0
            : currentDragProgress > 100
              ? 100
              : currentDragProgress
      } else {
        this.currentProgressBar =
          currentDragProgress < 0
            ? 0
            : currentDragProgress > 100
              ? 100
              : currentDragProgress
      }

      // Attach the listeners to the window
      window.addEventListener('mousemove', this.boundDrag)
      window.addEventListener('mouseup', this.boundEndDrag)
      window.addEventListener('touchmove', this.boundDrag)
      window.addEventListener('touchend', this.boundEndDrag)
    },
    drag (e) {
      if (!this.isDragging) return

      if (e.type === 'mousemove') {
        e.preventDefault()
      }

      const el =
        this.isDragging === 'volume'
          ? this.$refs.volumeProgressbar
          : this.$refs.progressbar
      const currentDragProgress =
        ((this.startLeft +
          (e.clientX || e.targetTouches[0].clientX) -
          this.startx) /
          el.offsetWidth) *
        100

      if (this.isDragging === 'volume') {
        this.currentAudioVolume =
          currentDragProgress < 0
            ? 0
            : currentDragProgress > 100
              ? 100
              : currentDragProgress
      } else {
        this.currentProgressBar =
          currentDragProgress < 0
            ? 0
            : currentDragProgress > 100
              ? 100
              : currentDragProgress
      }
    },
    endDrag (e) {
      if (!this.isDragging) return
      const isVolume = this.isDragging === 'volume'
      this.isDragging = null

      if (isVolume) {
        // Get parent and blur it
        const parent = this.$refs.volumeProgressbar.parentElement.parentElement
        parent.blur()
        this.$store.dispatch('setAudioVolume', this.currentAudioVolume)
      } else {
        this.audio.currentTime =
          (this.currentProgressBar / 100) * this.trackDuration

        this.getCurrentTimeEverySecond()
      }
      // Remove the listeners from the window
      window.removeEventListener('mousemove', this.boundDrag)
      window.removeEventListener('mouseup', this.boundEndDrag)
      window.removeEventListener('touchmove', this.boundDrag)
      window.removeEventListener('touchend', this.boundEndDrag)
    }
  },

  watch: {
    currentTrackId () {
      this.changeTrack()
    },
    currentProgressBar () {
      this.currentTime = Math.round(
        (this.currentProgressBar / 100) * this.trackDuration
      )
    },
    currentAudioVolume: {
      handler: function () {
        this.audio.volume = this.currentAudioVolume / 100
      },
      immediate: true
    },
    playStateAudioPlayerId (newVal) {
      if (newVal !== this.audioPlayerId) {
        this.stopAudio(false)
      }
    },
    playStateTrackId (newVal) {
      if (newVal !== this.currentTrackId && this.audioPlayerId === this.playStateAudioPlayerId) {
        this.currentTrackId = newVal
      }
    }
  },

  computed: {
    playlistIconClass () {
      return this.mobilePlayList
        ? 'fas fa-chevron-up'
        : 'fas fa-chevron-down'
    },
    isIOS () {
      const iOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
      return iOS
    },
    currentTrack () {
      return this.tracks.find((track) => track.id === this.currentTrackId)
    },
    nextTrack () {
      const currentTrackIndex = this.tracks.findIndex(
        (track) => track.id === this.currentTrackId
      )
      const nextTrackIndex = currentTrackIndex + 1
      if (nextTrackIndex < this.tracks.length) {
        return this.tracks[nextTrackIndex]
      }
      return null
    },
    canGoNext () {
      const currentTrackIndex = this.tracks.findIndex(
        (track) => track.id === this.currentTrackId
      )
      return currentTrackIndex < this.tracks.length - 1
    },
    canGoPrev () {
      const currentTrackIndex = this.tracks.findIndex(
        (track) => track.id === this.currentTrackId
      )
      return currentTrackIndex > 0
    },
    ...mapState({
      audioVolume: (state) => state.audio.audioVolume,
      playState: (state) => state.audio.playState
    }),
    ...mapGetters(['playStatePlaying', 'playStateTrackId', 'playStateAudioPlayerId']),
    currentlyPlaying () {
      return this.playStatePlaying && this.playStateAudioPlayerId === this.audioPlayerId
    }
  },

  created () {
    // Binding the method to 'this' in the created hook
    this.boundDrag = this.drag.bind(this)
    this.boundEndDrag = this.endDrag.bind(this)
  },
  beforeDestroy () {
    // Ensure that the event listeners are removed when the component is destroyed
    window.removeEventListener('mousemove', this.boundDrag)
    window.removeEventListener('mouseup', this.boundEndDrag)
  }
}
</script>
